import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { DIMENSIONALITY_VALUES } from 'constants/businessTool';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';
import { debounce } from 'lodash';
import { useGetPerformanceProfitability } from 'services/calculations/financialPerformance/hooks';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { dividePeriodsTableRows } from 'components/BusinessToolForm/utils/helpers';

import ToastService from 'helpers/ToastService';

import { IFormContentTypes, observedFields } from '../../utils/form';

const hasAllPerformanceProfitabilityFields = (requestData: Record<string, unknown>) => {
  return Object.values(requestData).every((value) => value !== undefined && value !== null);
};

export const usePerformanceProfitability = () => {
  const { id: businessPlanId = '' } = useParams();

  const { setValue } = useFormContext();

  const getPerformanceProfitability = useGetPerformanceProfitability();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MARKETING_AND_SALES,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKETING_AND_SALES.SALES_PLAN,
  );

  const [performanceProfitabilityFiled] = observedFields.performanceProfitability.setCalculatedValueFieldNames;

  const debouncedUpdateFunction = useMemo(
    () =>
      debounce(async (requestData, unit, netSales, grossProfit, netProfit) => {
        if (!hasAllPerformanceProfitabilityFields(requestData)) return;

        const params = {
          unit,
          businessPlanId,
          requestData,
        };

        try {
          const { EBITDA } = await getPerformanceProfitability.mutateAsync(params);
          const netMargin = dividePeriodsTableRows(grossProfit, netSales);
          const netProfitMargin = dividePeriodsTableRows(netProfit, netSales);

          const updatedProfitabilityTable = [netMargin, netProfitMargin, EBITDA];

          setValue(performanceProfitabilityFiled, updatedProfitabilityTable);
        } catch (error) {
          if (error instanceof Error) ToastService.showError(error.message);
        }
      }, 500),

    [],
  );

  const performanceProfitabilityHandler = useCallback(
    async (content: IFormContentTypes['performanceProfitability']) => {
      if (!canAccess) return;

      const [
        netProfitData,
        incomeTaxData,
        amortizationData,
        interestPaid,
        operationUnit,
        netSales,
        grossProfit,
        netProfit,
      ] = content;

      const profitabilityData = {
        netProfitData,
        incomeTaxData,
        amortizationData,
        interestPaid,
      };

      const unit = operationUnit ?? DIMENSIONALITY_VALUES.SINGLE;

      debouncedUpdateFunction(profitabilityData, unit, netSales, grossProfit, netProfit);
    },

    [debouncedUpdateFunction],
  );

  return performanceProfitabilityHandler;
};
